import { isEvolutionAppointment } from "@/components/schedule/evolution/isEvolutionAppointment";
import { isProntoAtendimentoAppointment } from "@/components/schedule/prontoAtendimento/mapProntoAppointmentToQueueItem";
import { useDict } from "@/utils/useDict";

/** Placeholder único quando não há fonte de verdade. Nunca "undefined". */
export const EMPTY_SPECIALTY_LABEL = "—";

export type AttendanceSpecialtyIndex = {
  encountersById: Map<string, any>;
  appointmentsById: Map<string, any>;
};

export type ResolveAttendanceSpecialtyInput = {
  encounter?: any;
  appointment?: any;
  snapshotHealthcare?: string;
  /** Só use no atendimento atual (gravação). Não aplicar o role do usuário logado em histórico de outro profissional. */
  roleSpecialty?: string;
};

export type QrDisplayInfo = {
  encounterId: string;
  healthcare: string;
  practitioner: string;
  identifier: string;
};

/** Texto utilizável como especialidade: não vazio, não a palavra "undefined"/"null". */
export function isValidSpecialtyText(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  return lower !== "undefined" && lower !== "null";
}

export function hasAttendanceSpecialty(value: string | undefined): boolean {
  return isValidSpecialtyText(value) && value !== EMPTY_SPECIALTY_LABEL;
}

function extractRefId(ref: any): string {
  if (!ref) return "";
  const value = typeof ref === "string" ? ref : ref?.reference;
  if (typeof value !== "string" || !value) return "";
  const parts = value.split("/");
  return parts[parts.length - 1] || "";
}

/** Encounter.appointment pode ser Reference ou Reference[]. */
export function extractAppointmentIdFromEncounter(encounter: any): string {
  const appt = encounter?.appointment;
  if (Array.isArray(appt)) return extractRefId(appt[0]);
  return extractRefId(appt);
}

export function extractEncounterIdFromQr(qr: any): string {
  return extractRefId(qr?.encounter);
}

/** Snapshot displayReferences do QR de atendimento (healthcare = item[0], practitioner = item[1]). */
export function extractQrSnapshot(qr: any): {
  healthcare: string;
  practitioner: string;
} {
  const displayReferences = qr?.item?.find(
    (e: any) => e?.linkId === "displayReferences",
  )?.item;
  const healthcare = displayReferences?.[0]?.answer?.[0]?.valueString;
  const practitioner = displayReferences?.[1]?.answer?.[0]?.valueString;
  return {
    healthcare: isValidSpecialtyText(healthcare) ? healthcare : "",
    practitioner: isValidSpecialtyText(practitioner) ? practitioner : "",
  };
}

/**
 * Lê Encounter.serviceType no shape do sumário (array + CodeableReference HCS)
 * e no atalho dos accordions de Observation ([0].reference.display),
 * além de concept.text quando o Encounter nasceu sem HCS (evolução/PA).
 */
export function extractServiceTypeDisplay(serviceType: any): string | undefined {
  const list = Array.isArray(serviceType)
    ? serviceType
    : serviceType
      ? [serviceType]
      : [];

  for (const el of list) {
    const nestedDisplay = el?.reference?.display;
    if (isValidSpecialtyText(nestedDisplay)) return nestedDisplay;

    const conceptText =
      el?.concept?.text || el?.concept?.coding?.[0]?.display;
    if (isValidSpecialtyText(conceptText)) return conceptText;

    if (isValidSpecialtyText(el?.display)) return el.display;
    if (isValidSpecialtyText(el?.text)) return el.text;
  }

  const hcs = list.find((el: any) =>
    el?.reference?.reference?.includes("HealthcareService"),
  );
  if (isValidSpecialtyText(hcs?.reference?.display)) {
    return hcs.reference.display;
  }

  return undefined;
}

function extractHcsDisplayFromAppointment(appointment: any): string | undefined {
  const actor = appointment?.participant?.find((p: any) =>
    p?.actor?.reference?.includes("HealthcareService"),
  )?.actor;
  return isValidSpecialtyText(actor?.display) ? actor.display : undefined;
}

/**
 * Cadeia obrigatória: Encounter.serviceType → Appointment HCS → snapshot válido → role à mão → "—".
 * Pura: não dispara GetEncounter/GetAppointment.
 */
export function resolveAttendanceSpecialty(
  input: ResolveAttendanceSpecialtyInput,
): string {
  const fromEncounter = extractServiceTypeDisplay(input.encounter?.serviceType);
  if (fromEncounter) return fromEncounter;

  const fromAppointment = extractHcsDisplayFromAppointment(input.appointment);
  if (fromAppointment) return fromAppointment;

  if (isValidSpecialtyText(input.snapshotHealthcare)) {
    return input.snapshotHealthcare;
  }

  if (isValidSpecialtyText(input.roleSpecialty)) {
    return input.roleSpecialty;
  }

  return EMPTY_SPECIALTY_LABEL;
}

/** Tipo de atendimento (Evolução, Pronto Atendimento, Encaixe, etc.). */
export function resolveAttendanceTypeLabel(
  encounter?: any,
  appointment?: any,
): string {
  const encounterCode =
    encounter?.type?.[0]?.coding?.[0]?.code || encounter?.type?.[0]?.text;
  const appointmentCode =
    appointment?.appointmentType?.coding?.[0]?.code ||
    appointment?.appointmentType?.text;
  const code = encounterCode || appointmentCode;

  if (typeof code === "string" && useDict.appointmentType[code]) {
    return useDict.appointmentType[code];
  }

  if (isEvolutionAppointment(appointment)) {
    return useDict.appointmentType.EVOLUTION;
  }
  if (isProntoAtendimentoAppointment(appointment)) {
    return useDict.appointmentType.prontoatendimento;
  }

  const text =
    encounter?.type?.[0]?.text || appointment?.appointmentType?.text;
  return isValidSpecialtyText(text) ? text : EMPTY_SPECIALTY_LABEL;
}

/**
 * Substitui o parse repetido de displayReferences nos históricos.
 * healthcare vazio (não "—") para o chip atual `|| "Sem especialidade"` continuar válido.
 */
export function buildQrDisplayInfo(
  qr: any,
  index?: AttendanceSpecialtyIndex,
): QrDisplayInfo {
  const snapshot = extractQrSnapshot(qr);
  const encounterId = extractEncounterIdFromQr(qr);
  const encounter = encounterId
    ? index?.encountersById?.get(encounterId)
    : undefined;
  const appointmentId = extractAppointmentIdFromEncounter(encounter);
  const appointment = appointmentId
    ? index?.appointmentsById?.get(appointmentId)
    : undefined;

  const specialty = resolveAttendanceSpecialty({
    encounter,
    appointment,
    snapshotHealthcare: snapshot.healthcare,
  });

  return {
    encounterId,
    healthcare: specialty === EMPTY_SPECIALTY_LABEL ? "" : specialty,
    practitioner: snapshot.practitioner,
    identifier: qr?.author?.reference?.split("-")?.[1] || "",
  };
}
